import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { appSelector } from "../../redux/selector";

import {
  Avatar,
  Badge,
  Box,
  ListItemButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  BusinessCenterRounded,
  CancelRounded,
  CheckCircleRounded,
} from "@mui/icons-material";
import moment from "moment";
import { userSelector } from "../../user-redux/selector";
import { styled } from "@mui/material/styles";
import {
  getJobDetailsAction,
  getSubmissionResumeDetails,
  selectedResume,
  selectedSubmissionAction,
  selectedTabViewAction,
  updateViewedStatus,
} from "../redux/action";
import { myCandidatesSelector } from "../redux/selector";
import { limit } from "../../utils/constantValues";
import axios from "axios";
import * as types from "../redux/actionTypes";
import SubmissionStatus from "./SubmissionStatus";

const Submissions = ({ refreshKey }) => {
  const dispatch = useDispatch();
  const { candidateSubmissions, selectedSubmission } = useSelector(appSelector);
  const { candidateDetails } = useSelector(myCandidatesSelector);
  const { user } = useSelector(userSelector);

  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const theme = useTheme();
  const isDesktopLarge = useMediaQuery(theme.breakpoints.up("lg"));

  const [userTypeId, setUserTypeId] = useState(null);
  const [selfSubmissions, setSelfSubmissions] = useState([]);
  const [otherRecSubmissions, setOtherRecSubmissions] = useState([]);

  const [viewMode, setViewMode] = useState("self");
  const shouldSelectLatestRef = useRef(true);
  const prevCandidateIdRef = useRef(candidateDetails?.candidate_id);

  const submissionFromList = (listItem, selected) => {
    if (!listItem) return listItem;
    if (!selected || listItem.id !== selected.id) return listItem;
    return {
      ...listItem,
      bench_status_id: selected.bench_status_id ?? listItem.bench_status_id,
      pStatusName: selected.pStatusName ?? listItem.pStatusName,
      nStatusName: selected.nStatusName ?? listItem.nStatusName,
      s_rate: selected.s_rate ?? listItem.s_rate,
    };
  };

  const selectSubmission = (submission) => {
    if (!submission) return;
    dispatch(selectedTabViewAction("Submission"));
    dispatch(selectedResume({}));
    dispatch(selectedSubmissionAction(submission));
    dispatch(getJobDetailsAction(submission?.job_id));
    if (submission?.resume_id) {
      dispatch(getSubmissionResumeDetails(submission?.resume_id));
    }
    if (Number(submission?.unread_count) > 0) {
      submission.unread_count = 0;
      dispatch(updateViewedStatus(submission?.candidate_id, submission?.job_id));
    }
  };

  const fetchSubmissions = async () => {
    if (!candidateDetails?.candidate_id || !user?.id) {
      dispatch({
        type: types.GET_CANDIDATE_SUBMISSIONS_ACTION,
        payload: [],
        self_submissions: [],
        other_rec_submissions: [],
        user_type_id: null,
      });
      setUserTypeId(null);
      setSelfSubmissions([]);
      setOtherRecSubmissions([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      const res = await axios.get(
        `https://bsapi.jobsnprofiles.com/candidate/${user.id}/${candidateDetails.candidate_id}/admin_rec_submissions/0/1000`
      );

      const root = res?.data || {};
      const payload = root.data || root;

      const typeId = payload?.user_type_id ?? null;
      setUserTypeId(typeId);

      let reduxSubmissions = [];

      if (typeId === 13 || typeId === "13") {
        const self = payload?.self_submissions || [];
        const others = payload?.other_rec_submissions || [];

        setSelfSubmissions(self);
        setOtherRecSubmissions(others);
        reduxSubmissions = [...self, ...others];
        setTotalCount(payload?.total_count || reduxSubmissions.length);

        dispatch({
          type: types.GET_CANDIDATE_SUBMISSIONS_ACTION,
          payload: reduxSubmissions,
          self_submissions: self,
          other_rec_submissions: others,
          user_type_id: typeId,
        });
      } else {
        const subs = payload?.submissions || [];
        setSelfSubmissions([]);
        setOtherRecSubmissions([]);

        reduxSubmissions = subs;
        setTotalCount(payload?.total_count || subs.length);

        dispatch({
          type: types.GET_CANDIDATE_SUBMISSIONS_ACTION,
          payload: reduxSubmissions,
          self_submissions: [],
          other_rec_submissions: [],
          user_type_id: typeId,
        });
      }
    } catch (err) {
      console.error("Error fetching submissions", err);
      dispatch({
        type: types.GET_CANDIDATE_SUBMISSIONS_ACTION_FAIL,
        payload: err,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    // Clear previous candidate submission UI immediately to avoid stale flash
    dispatch(selectedSubmissionAction(null));
    dispatch({
      type: types.GET_CANDIDATE_SUBMISSIONS_ACTION,
      payload: [],
      self_submissions: [],
      other_rec_submissions: [],
      user_type_id: null,
    });
    setSelfSubmissions([]);
    setOtherRecSubmissions([]);
    setUserTypeId(null);
    setTotalCount(0);

    if (prevCandidateIdRef.current !== candidateDetails?.candidate_id) {
      shouldSelectLatestRef.current = true;
      prevCandidateIdRef.current = candidateDetails?.candidate_id;
    }
    fetchSubmissions();
  }, [candidateDetails?.candidate_id, user?.id, refreshKey]);

  const sortedAllSubmissions = useMemo(() => {
    let baseList = [];

    if (userTypeId === 13 || userTypeId === "13") {
      if (viewMode === "self") {
        baseList = [...selfSubmissions];
      } else if (viewMode === "others") {
        baseList = [...otherRecSubmissions];
      } else {
        baseList = [...selfSubmissions, ...otherRecSubmissions];
      }
    } else {
      baseList = [...(candidateSubmissions || [])];
    }

    return baseList.sort((a, b) => {
      const getTime = (sub) => {
        const dates = [sub?.modified, sub?.created, sub?.submitted_date]
          .filter(Boolean)
          .map((d) => new Date(d).getTime())
          .filter((t) => !Number.isNaN(t));
        return dates.length ? Math.max(...dates) : 0;
      };
      return getTime(b) - getTime(a);
    });
  }, [
    userTypeId,
    viewMode,
    selfSubmissions,
    otherRecSubmissions,
    candidateSubmissions,
  ]);

  const listToRender = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedAllSubmissions.slice(startIndex, endIndex);
  }, [sortedAllSubmissions, page, rowsPerPage]);


  const getTotalCount = useMemo(() => {
    if (userTypeId === 13 || userTypeId === "13") {
      if (viewMode === "self") return selfSubmissions.length;
      if (viewMode === "others") return otherRecSubmissions.length;
      return selfSubmissions.length + otherRecSubmissions.length;
    }
    return candidateSubmissions?.length || 0;
  }, [userTypeId, viewMode, selfSubmissions, otherRecSubmissions, candidateSubmissions]);

  const totalPages = Math.ceil(getTotalCount / rowsPerPage);

  const handleSelection = (submission) => {
    if (submission?.id === selectedSubmission?.id) return;
    shouldSelectLatestRef.current = false;
    selectSubmission(submission);
  };

  useEffect(() => {
    if (isLoading) return;

    if (sortedAllSubmissions.length > 0 && candidateDetails?.candidate_id) {
      const urlParams = new URLSearchParams(window.location.search);
      const targetJobId = urlParams.get("job") || sessionStorage.getItem("target_job_id");

      if (targetJobId) {
        const targetSubmission = sortedAllSubmissions.find(
          (sub) => String(sub.job_id) === String(targetJobId)
        );

        if (targetSubmission) {
          sessionStorage.removeItem("target_job_id");
          if (urlParams.has("job")) {
            urlParams.delete("job");
            const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
            window.history.replaceState({}, '', newUrl);
          }

          shouldSelectLatestRef.current = false;
          if (targetSubmission.id !== selectedSubmission?.id) {
            selectSubmission(targetSubmission);
          } else {
            const synced = submissionFromList(targetSubmission, selectedSubmission);
            if (JSON.stringify(synced) !== JSON.stringify(selectedSubmission)) {
              dispatch(selectedSubmissionAction(synced));
            }
          }
          return;
        }
      }

      if (shouldSelectLatestRef.current) {
        shouldSelectLatestRef.current = false;
        selectSubmission(sortedAllSubmissions[0]);
        return;
      }

      const currentExists = sortedAllSubmissions.find(
        (sub) => sub.id === selectedSubmission?.id
      );

      if (currentExists) {
        const synced = submissionFromList(currentExists, selectedSubmission);
        if (JSON.stringify(synced) !== JSON.stringify(selectedSubmission)) {
          dispatch(selectedSubmissionAction(synced));
        }
        return;
      }

      selectSubmission(sortedAllSubmissions[0]);
    } else {
      dispatch(getJobDetailsAction(null));
      dispatch(selectedSubmissionAction(null));
    }
  }, [sortedAllSubmissions, candidateDetails?.candidate_id, dispatch, selectedSubmission, isLoading]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1); // Reset to first page when changing rows per page
  };

  const containerStyles = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const isAdminUser = Number(userTypeId) === 13;
  const isSelfView = viewMode === "self";

  return (
    <Box sx={containerStyles}>
      {(userTypeId === 13 || userTypeId === "13") && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            p: 1,
          }}
        >
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(e, val) => {
              if (val) {
                setViewMode(val);
                setPage(1);
              }
            }}
          >
            <ToggleButton value="self" sx={{ fontSize: 10, px: 4 }}>
              Self
            </ToggleButton>
            <ToggleButton value="others" sx={{ fontSize: 10, px: 4 }}>
              Others
            </ToggleButton>
            <ToggleButton value="all" sx={{ fontSize: 10, px: 4 }}>
              All
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : !candidateDetails?.candidate_id ? (
          <p style={{ textAlign: "center", margin: 12, fontSize: 12 }}>
            <b>Please select a candidate to view submissions</b>
          </p>
        ) : !listToRender || listToRender?.length === 0 ? (
          <p style={{ textAlign: "center", margin: 12, fontSize: 12 }}>
            <b>No submissions yet for this contact</b>
          </p>
        ) : (
          listToRender?.map((submission, index) => (
            <div key={index}>
              <ListItemButton
                selected={selectedSubmission?.id === submission?.id}
                sx={{
                  margin: "0px",
                  " .hide-icons": {
                    display: "none",
                  },
                  "&:hover": {
                    ".hide-icons": {
                      display: "block",
                    },
                  },
                }}
                onClick={() => handleSelection(submission)}
              >
                <div style={{ display: "flex", gap: 10, width: "100%" }}>
                  <Avatar
                    sx={{
                      height: isDesktopLarge ? "24px" : "35px",
                      width: isDesktopLarge ? "24px" : "35px",
                    }}
                  >
                    <BusinessCenterRounded
                      sx={{
                        color: "#fff",
                        height: isDesktopLarge ? "16px" : "20px",
                        width: isDesktopLarge ? "16px" : "20px",
                      }}
                    />
                  </Avatar>
                  <Stack direction="column" sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Tooltip title={submission?.jobtitle} placement="top">
                        <Typography
                          sx={{
                            fontSize: isDesktopLarge ? "12px" : "10px",
                            fontWeight: 600,
                            margin: 0,
                            minWidth: isDesktopLarge ? "150px" : "80px",
                            maxWidth: isDesktopLarge ? "150px" : "80px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {submission?.jobtitle}
                        </Typography>
                      </Tooltip>
                      {submission.unread_count > 0 && (
                        <Box
                          sx={{
                            ml: 1,
                            px: 0.9,
                            py: 0.4,
                            background: "linear-gradient(to right, #2196F3, #64B5F6)",
                            color: "white",
                            fontSize: "9px",
                            fontWeight: 700,
                            borderRadius: "50px",
                            letterSpacing: "0.4px",
                          }}
                        >
                          1
                        </Box>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: isDesktopLarge ? "12px" : "9px",
                        margin: 0,
                        textTransform: "capitalize",
                      }}
                    >
                      {submission?.company_name
                        ? submission?.company_name.toString()
                        : "N/A"}
                    </Typography>
                    {isAdminUser && !isSelfView && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: isDesktopLarge ? "11px" : "9px",
                            color: "text.secondary",
                            margin: 0,
                          }}
                        >
                          Recruiter
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: isDesktopLarge ? "11px" : "9px",
                            fontWeight: 600,
                            color: "primary.main",
                            margin: 0,
                            textTransform: "capitalize",
                            maxWidth: isDesktopLarge ? "180px" : "120px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {submission?.recruiter_name
                            ? submission?.recruiter_name.toString()
                            : "N/A"}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}>
                      <SubmissionStatus
                        submissionId={submission.submission_id}
                        jobId={submission?.job_id}
                        pStatusName={submission?.pStatusName}
                        isSelected={selectedSubmission?.id === submission?.id}
                        isDesktopLarge={isDesktopLarge}
                      />
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: "10px",
                          fontWeight: "400",
                          color: "rgb(0 0 0 / 50%)",
                        }}
                      >
                        {submission?.modified
                          ? moment(submission.modified).fromNow()
                          : "N/A"}
                      </Typography>
                    </Box>
                  </Stack>
                </div>
              </ListItemButton>
            </div>
          ))
        )}
      </Box>

      {!isLoading && listToRender && listToRender.length > 0 && getTotalCount > 10 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: 1,
            borderTop: "1px solid rgba(0, 0, 0, 0.12)",
            bgcolor: "#fff",
          }}
        >
          <Pagination
            count={totalPages || 1}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="small"
            sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}
            siblingCount={0}
            boundaryCount={1}
          />
          {getTotalCount > 10 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 2 }}>
              <Typography variant="body2" sx={{ mr: 1 }}>Rows per page:</Typography>
              <Select
                size="small"
                variant="standard"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                sx={{ width: 60 }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Submissions;

const RedStyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#ff0000",
    color: "#ff0000",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      right: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      // animation: "ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  // "@keyframes ripple": {
  //   "0%": {
  //     transform: "scale(.8)",
  //     opacity: 1,
  //   },
  //   "100%": {
  //     transform: "scale(2.4)",
  //     opacity: 0,
  //   },
  // },
}));